import { useCallback, useEffect, useState } from "react";
import { listPollers, mailboxLabel, runPoller, type Mailbox, type MailboxInfo, type Poller, type PollRun } from "../api";
import { formatTimestamp } from "../format";
import { FunctionLogs } from "./FunctionLogs";
import { UserAccess } from "./UserAccess";

// The administration board: one card per login, and the one thing an operator
// can DO from this interface — run that login's SFTP poller now rather than on
// the hour. Below it, the functions' logs, so what a run (or the hour, or the
// processor) did can be read right here. Everything else is reading; the
// trigger is the exception, and the page says so in as many words.

type Run =
  | { state: "running"; startedAt: string }
  | { state: "done"; run: PollRun }
  /** The request itself failed — a 409 collision, a network error. */
  | { state: "refused"; startedAt: string; error: string };

const message = (err: unknown): string => (err instanceof Error ? err.message : String(err));

export function AdminBoard({
  token,
  viewer,
  mailboxes,
  onShowReceived,
}: {
  token: string;
  /** The signed-in user's email — the one account the board will not remove. */
  viewer: string;
  mailboxes: MailboxInfo[];
  /** Takes the operator to the received files of one login, freshly listed. */
  onShowReceived: (mailbox: Mailbox) => void;
}): JSX.Element {
  const [pollers, setPollers] = useState<Poller[] | undefined>();
  const [loadError, setLoadError] = useState<string | undefined>();
  const [runs, setRuns] = useState<Partial<Record<Mailbox, Run>>>({});

  useEffect(() => {
    let cancelled = false;
    listPollers(token)
      .then((list) => {
        if (!cancelled) setPollers(list);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(message(err));
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const trigger = useCallback(
    (mailbox: Mailbox) => {
      const startedAt = new Date().toISOString();
      setRuns((previous) => ({ ...previous, [mailbox]: { state: "running", startedAt } }));
      runPoller(token, mailbox)
        .then((run) => setRuns((previous) => ({ ...previous, [mailbox]: { state: "done", run } })))
        .catch((err: unknown) =>
          setRuns((previous) => ({ ...previous, [mailbox]: { state: "refused", startedAt, error: message(err) } })),
        );
    },
    [token],
  );

  return (
    <main className="admin">
      <h2>Admin</h2>
      <p className="doc-lede">
        The files the bank puts up for us are fetched <b>once an hour</b>. This board runs that fetch now —
        for one login at a time — so a file the bank has just published can be read without waiting.
      </p>
      <p className="admin-note">
        A run does exactly what the hour does: connects with the login, copies every file in its pickup
        folder into this service (which publishes the events for them), and removes the files from the
        bank's server. Only one poll of a login runs at a time — a run that collides with the hourly one is
        refused, not doubled. A login not on the hourly schedule cannot be run from here at all.
      </p>

      <h3 className="admin-heading">Fetch from the bank</h3>
      {loadError ? (
        <p className="error">The pollers could not be listed: {loadError}</p>
      ) : pollers === undefined ? (
        <p className="admin-status">Loading…</p>
      ) : (
        <div className="admin-cards">
          {pollers.map((poller) => (
            <PollerCard
              key={poller.mailbox}
              poller={poller}
              info={mailboxes.find((m) => m.id === poller.mailbox) ?? { id: poller.mailbox, subsystem: poller.subsystem }}
              run={runs[poller.mailbox]}
              onRun={() => trigger(poller.mailbox)}
              onShowReceived={() => onShowReceived(poller.mailbox)}
            />
          ))}
        </div>
      )}

      <h3 className="admin-heading">Who has access</h3>
      <p className="admin-note">
        The accounts that can sign in here. There is no sign-up: this list is the whole of it. Inviting
        someone emails them a temporary password; removing someone ends their access at once. You cannot
        remove yourself, or the last account.
      </p>
      <UserAccess token={token} viewer={viewer} />

      <h3 className="admin-heading">Logs</h3>
      <p className="admin-note">
        What each function of the service wrote, newest first, read from CloudWatch as you ask for it. The
        service's own lines carry a level and the facts of the event; the runtime's <i>START</i> / <i>END</i> /{" "}
        <i>REPORT</i> lines are shown dimmed. Narrowing by level or word happens here, over the lines read.
      </p>
      <FunctionLogs token={token} mailboxes={mailboxes} />
    </main>
  );
}

function PollerCard({
  poller,
  info,
  run,
  onRun,
  onShowReceived,
}: {
  poller: Poller;
  info: MailboxInfo;
  run: Run | undefined;
  onRun: () => void;
  onShowReceived: () => void;
}): JSX.Element {
  const running = run?.state === "running";
  // Only a login this service polls on the hour can be run from here (the API
  // refuses the rest too): one deployed dark may still be read by another
  // poller, and a run would fetch and delete its files.
  const runnable = poller.deployed && poller.scheduled;
  return (
    <section className="admin-card" aria-labelledby={`poller-${poller.mailbox}`}>
      <div className="admin-card-head">
        <h3 id={`poller-${poller.mailbox}`}>{mailboxLabel(info)}</h3>
        <span className="chip" data-tone={poller.deployed ? (poller.scheduled ? undefined : "quiet") : "bad"}>
          {!poller.deployed ? "No poller here" : poller.scheduled ? "Polled every hour" : "Not on the hourly schedule"}
        </span>
      </div>
      <p className="admin-card-text">
        {!poller.deployed
          ? "This environment has no SFTP poller for this login, so nothing can be fetched from here."
          : poller.scheduled
            ? "The hourly schedule runs this login's poller. Running it now fetches whatever the bank has put up since the last hour."
            : `${poller.mailbox} is not polled by this service yet, so it cannot be fetched from here: a run would fetch and delete files another poller may still need. Its login is proven with its login check.`}
      </p>
      <button type="button" className="button" disabled={!runnable || running} onClick={onRun}>
        {running ? "Fetching…" : "Fetch new files now"}
      </button>
      {run ? <RunReport run={run} onShowReceived={onShowReceived} /> : null}
    </section>
  );
}

function RunReport({ run, onShowReceived }: { run: Run; onShowReceived: () => void }): JSX.Element {
  if (run.state === "running") {
    return (
      <div className="admin-run" aria-live="polite">
        <span className="label">Started {formatTimestamp(run.startedAt)}</span>
        <p>Connecting to the bank…</p>
      </div>
    );
  }
  if (run.state === "refused") {
    return (
      <div className="admin-run" aria-live="polite">
        <span className="label">Started {formatTimestamp(run.startedAt)}</span>
        <p className="error">{run.error}</p>
      </div>
    );
  }

  const { run: result } = run;
  const when = `Started ${formatTimestamp(result.startedAt)}${result.durationMs !== undefined ? ` · ${formatDuration(result.durationMs)}` : ""}`;

  if (result.outcome === "running") {
    return (
      <div className="admin-run" aria-live="polite">
        <span className="label">{when}</span>
        <p>
          Still running after twenty seconds — a slow connection, or a lot of files. The files will appear
          under <i>Received</i> as they land; look again in a minute.
        </p>
        <button type="button" className="linkish" onClick={onShowReceived}>
          Show received files
        </button>
      </div>
    );
  }
  if (result.outcome === "failed") {
    return (
      <div className="admin-run" aria-live="polite">
        <span className="label">{when}</span>
        <p className="error">The poller failed: {result.error ?? "no reason given"}</p>
      </div>
    );
  }

  const fetched = result.fetched ?? [];
  const failed = result.failed ?? [];
  return (
    <div className="admin-run" aria-live="polite">
      <span className="label">{when}</span>
      <p>
        {fetched.length === 0
          ? "Nothing new — the bank's pickup folder was empty."
          : `Fetched ${fetched.length} file${fetched.length === 1 ? "" : "s"}.`}
        {failed.length > 0 ? ` ${failed.length} could not be fetched and ${failed.length === 1 ? "is" : "are"} still on the bank's server.` : ""}
      </p>
      {fetched.length > 0 ? <FileNames names={fetched} /> : null}
      {failed.length > 0 ? <FileNames names={failed} tone="bad" /> : null}
      {fetched.length > 0 ? (
        <button type="button" className="linkish" onClick={onShowReceived}>
          Show received files
        </button>
      ) : null}
    </div>
  );
}

function FileNames({ names, tone }: { names: string[]; tone?: "bad" }): JSX.Element {
  return (
    <ul className="file-names">
      {names.map((name) => (
        <li key={name} className="chip mono" data-tone={tone}>
          {name}
        </li>
      ))}
    </ul>
  );
}

const formatDuration = (ms: number): string => (ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`);
